import { Kbd } from "@heroui/react";
import { Command } from "@heroui-pro/react";
import { Plus, Search } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { COMMAND_ACTIONS, NAV_GROUPS, SHORT_GO_TO } from "./nav";

export function CommandMenu({
  isOpen,
  onOpenChange,
}: {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (!isOpen) setQuery("");
  }, [isOpen]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key.toLowerCase() === "k" && (event.metaKey || event.ctrlKey)) {
        event.preventDefault();
        onOpenChange(!isOpen);
      }
    };

    window.addEventListener("keydown", onKeyDown);

    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isOpen, onOpenChange]);

  const go = (href: string) => {
    onOpenChange(false);
    navigate(href);
  };

  return (
    <Command>
      <Command.Backdrop
        isOpen={isOpen}
        variant="blur"
        onOpenChange={onOpenChange}
      >
        <Command.Container size="lg">
          <Command.Dialog
            aria-label="Search pages and actions"
            className="max-h-[min(80vh,640px)]"
            inputValue={query}
            onInputChange={setQuery}
          >
            <Command.Header>
              <Command.InputGroup>
                <Command.InputGroup.Prefix>
                  <Search className="size-4" />
                </Command.InputGroup.Prefix>
                <Command.InputGroup.Input placeholder="Search pages and actions" />
                <Command.InputGroup.Suffix>
                  <Kbd>
                    <Kbd.Content>esc</Kbd.Content>
                  </Kbd>
                </Command.InputGroup.Suffix>
              </Command.InputGroup>
            </Command.Header>
            <Command.List
              // eslint-disable-next-line jsx-a11y/no-autofocus -- react-aria menu prop: highlight the first match
              autoFocus="first"
              renderEmptyState={() => (
                <p className="text-muted px-3 py-6 text-center text-sm">
                  No results
                </p>
              )}
            >
              <Command.Group heading="Go to">
                {NAV_GROUPS.flatMap((group) =>
                  group.items
                    .filter(
                      (item) => query !== "" || SHORT_GO_TO.includes(item.href),
                    )
                    .map((item) => {
                      // Scraped products / categories / brands share names with the catalog pages.
                      const label =
                        group.label === "Scraped"
                          ? `Scraped ${item.label.toLowerCase()}`
                          : item.label;

                      return (
                        <Command.Item
                          key={item.href}
                          id={item.href}
                          textValue={`${label} ${group.label}`}
                          onAction={() => go(item.href)}
                        >
                          <item.icon className="text-muted size-4" />
                          {label}
                          <span className="text-muted ms-auto text-xs">
                            {group.label}
                          </span>
                        </Command.Item>
                      );
                    }),
                )}
              </Command.Group>
              <Command.Group heading="Actions">
                {COMMAND_ACTIONS.map((action) => (
                  <Command.Item
                    key={action.href}
                    id={action.href}
                    textValue={action.label}
                    onAction={() => go(action.href)}
                  >
                    <Plus className="text-muted size-4" />
                    {action.label}
                    <span className="text-muted ms-auto text-xs">
                      {action.group}
                    </span>
                  </Command.Item>
                ))}
              </Command.Group>
            </Command.List>
            <Command.Footer className="text-muted flex items-center gap-4 text-xs">
              <span className="flex items-center gap-1.5">
                <Kbd>
                  <Kbd.Abbr keyValue="up" />
                </Kbd>
                <Kbd>
                  <Kbd.Abbr keyValue="down" />
                </Kbd>
                Navigate
              </span>
              <span className="flex items-center gap-1.5">
                <Kbd>
                  <Kbd.Abbr keyValue="enter" />
                </Kbd>
                Open
              </span>
              <span className="ms-auto flex items-center gap-1.5">
                <Kbd>
                  <Kbd.Content>esc</Kbd.Content>
                </Kbd>
                Close
              </span>
            </Command.Footer>
          </Command.Dialog>
        </Command.Container>
      </Command.Backdrop>
    </Command>
  );
}
